import { Grid, Typography, Card, CardContent } from "@mui/material";
import { FaMotorcycle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import TwoWheeler from "@mui/icons-material/TwoWheeler";
import Build from "@mui/icons-material/Build";
import Favorite from "@mui/icons-material/Favorite";
import { motion } from "framer-motion";
import { HiSparkles, HiArrowTrendingUp, HiShieldCheck, HiHeart } from "react-icons/hi2";

const CustomerHome = () => {
  const navigate = useNavigate();
  const handleButtonClick = (path) => () => {
    navigate(path);
  };

  return (
    <div className="min-h-screen gradient-bg pt-20 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-soft mb-6"
          >
            <HiSparkles className="w-5 h-5 text-accent-500" />
            <span className="text-sm font-semibold text-gray-700">Customer Dashboard</span>
          </motion.div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Welcome to Your
            <span className="text-gradient block">Bike Hub</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore, discover, and manage your bike journey with our comprehensive platform
          </p>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          <Card className="card card-hover text-center p-6">
            <CardContent>
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HiArrowTrendingUp className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Listings</h3>
              <p className="text-3xl font-bold text-primary-600">150+</p>
            </CardContent>
          </Card>
          
          <Card className="card card-hover text-center p-6">
            <CardContent>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HiShieldCheck className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verified Sellers</h3>
              <p className="text-3xl font-bold text-green-600">50+</p>
            </CardContent>
          </Card>
          
          <Card className="card card-hover text-center p-6">
            <CardContent>
              <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HiHeart className="w-6 h-6 text-accent-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Happy Customers</h3>
              <p className="text-3xl font-bold text-accent-600">200+</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Main Actions Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Quick Actions</h2>
            <p className="text-gray-600">Choose what you'd like to do today</p>
          </div>
          
          <Grid container spacing={4} justifyContent="center">
            {buttons.map((button, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Card
                    className="card card-hover cursor-pointer h-full"
                    onClick={handleButtonClick(button.path)}
                  >
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-primary-200">
                        {button.icon}
                      </div>
                      <Typography
                        variant="h6"
                        className="font-bold text-gray-900 mb-2"
                      >
                        {button.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="text-gray-600"
                      >
                        {button.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Recent Activity Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-16"
        >
          <Card className="card">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Recent Activity</h3>
                <p className="text-gray-600">Stay updated with your latest interactions</p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <TwoWheeler className="text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Browsed Used Bikes</p>
                    <p className="text-sm text-gray-500">2 hours ago</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                    <Favorite className="text-accent-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Added to Wishlist</p>
                    <p className="text-sm text-gray-500">1 day ago</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Build className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Found Workshop</p>
                    <p className="text-sm text-gray-500">3 days ago</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

const buttons = [
  {
    label: "Used Bikes",
    icon: <FaMotorcycle style={{ fontSize: 32, color: "#3b82f6" }} />,
    path: "/buy-used-bike",
    description: "Browse pre-owned bikes"
  },
  {
    label: "New Bikes",
    icon: <TwoWheeler style={{ fontSize: 32, color: "#10b981" }} />,
    path: "/buy-bike",
    description: "Explore brand new models"
  },
  {
    label: "Workshops",
    icon: <Build style={{ fontSize: 32, color: "#f59e0b" }} />,
    path: "/workshops",
    description: "Find service centers"
  },
  {
    label: "Wish List",
    icon: <Favorite style={{ fontSize: 32, color: "#ef4444" }} />,
    path: "/wish-list",
    description: "Your saved favorites"
  },
];

export default CustomerHome;
